import { useState, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Package,
  Truck,
  Lock,
  BarChart3,
  Plus,
  X,
  Loader2,
  Check,
  AlertTriangle,
  Send,
  ClipboardList,
  RefreshCw,
  Pencil,
  ShieldCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useCashierCountByToken, type CashierCountItem } from '@/hooks/useCashierCounts';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

type Phase = 'opening' | 'received' | 'closing';
type ActiveView = Phase | 'summary';

function formatTime(iso: string | null): string {
  if (!iso) return '--';
  const d = new Date(iso);
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function calcItem(item: CashierCountItem) {
  const o = item.opening_qty ?? 0;
  const r = item.received_qty ?? 0;
  const c = item.closing_qty;
  const available = o + r;
  const expectedSold = c !== null ? available - c : null;
  const systemSold = item.system_sold_qty ?? null;
  const variance = expectedSold !== null && systemSold !== null ? expectedSold - systemSold : null;
  return { available, expectedSold, systemSold, variance };
}

export default function CashierCount() {
  const { token } = useParams<{ token: string }>();
  const {
    count,
    isLoading,
    error,
    updateItem,
    refreshReceived,
    lockPhase,
    setCashierName,
    addItem,
    removeItem,
    completeCount,
    sendReport,
  } = useCashierCountByToken(token);

  const [activeView, setActiveView] = useState<ActiveView>('opening');
  const [newItemName, setNewItemName] = useState('');
  const [cashierNameInput, setCashierNameInput] = useState('');
  const [cashierNameSaved, setCashierNameSaved] = useState(false);

  // Adjustment dialog state
  const [adjustDialog, setAdjustDialog] = useState<{
    itemId: string;
    itemName: string;
    field: 'opening_qty' | 'received_qty';
    systemValue: number;
    currentValue: number;
  } | null>(null);
  const [adjustValue, setAdjustValue] = useState('');
  const [adjustReason, setAdjustReason] = useState('');

  // Completion checklist dialog
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);

  // Sync cashier name input when data loads
  useEffect(() => {
    if (count?.cashier_name && !cashierNameSaved) {
      setCashierNameInput(count.cashier_name);
      setCashierNameSaved(true);
    }
  }, [count?.cashier_name, cashierNameSaved]);

  const handleCashierNameBlur = useCallback(() => {
    if (cashierNameInput.trim() && cashierNameInput !== count?.cashier_name) {
      setCashierName.mutate(cashierNameInput.trim());
      setCashierNameSaved(true);
    }
  }, [cashierNameInput, count?.cashier_name, setCashierName]);

  const handleUpdateItem = useCallback(
    (itemId: string, field: 'opening_qty' | 'received_qty' | 'closing_qty', value: string, adjustmentReason?: string) => {
      const numValue = value === '' ? 0 : Math.max(0, parseInt(value) || 0);
      updateItem.mutate({ itemId, field, value: numValue, adjustmentReason }, {
        onError: (err: any) => {
          toast.error(err.message || 'Failed to update');
        },
      });
    },
    [updateItem]
  );

  const handleOpenAdjust = useCallback(
    (itemId: string, itemName: string, field: 'opening_qty' | 'received_qty', systemValue: number, currentValue: number) => {
      setAdjustDialog({ itemId, itemName, field, systemValue, currentValue });
      setAdjustValue(String(currentValue));
      setAdjustReason('');
    },
    []
  );

  const handleConfirmAdjust = useCallback(() => {
    if (!adjustDialog) return;
    const numValue = Math.max(0, parseInt(adjustValue) || 0);
    if (!adjustReason.trim()) {
      toast.error('A reason is required when adjusting system-provided values');
      return;
    }
    handleUpdateItem(adjustDialog.itemId, adjustDialog.field, String(numValue), adjustReason.trim());
    setAdjustDialog(null);
  }, [adjustDialog, adjustValue, adjustReason, handleUpdateItem]);

  const handleRefreshReceived = useCallback(() => {
    refreshReceived.mutate(undefined, {
      onSuccess: () => {
        toast.success('Received quantities updated from system');
      },
      onError: (err: any) => {
        toast.error(err.message || 'Failed to refresh');
      },
    });
  }, [refreshReceived]);

  const handleLockPhase = useCallback(
    (phase: Phase) => {
      lockPhase.mutate(phase, {
        onSuccess: () => {
          toast.success(`${phase.charAt(0).toUpperCase() + phase.slice(1)} stock locked`);
        },
        onError: (err: any) => {
          toast.error(err.message || 'Failed to lock phase');
        },
      });
    },
    [lockPhase]
  );

  const handleAddItem = useCallback(() => {
    if (!newItemName.trim()) return;
    addItem.mutate(newItemName.trim(), {
      onSuccess: () => {
        setNewItemName('');
      },
      onError: (err: any) => {
        toast.error(err.message || 'Failed to add item');
      },
    });
  }, [newItemName, addItem]);

  const handleRemoveItem = useCallback(
    (itemId: string) => {
      removeItem.mutate(itemId);
    },
    [removeItem]
  );

  const handleComplete = useCallback(() => {
    setShowCompleteDialog(false);
    completeCount.mutate(undefined, {
      onSuccess: () => {
        toast.success('Stock count completed and inventory updated');
        sendReport.mutate(undefined, {
          onSuccess: () => {
            toast.success('Report sent to admin');
          },
          onError: () => {
            toast.error('Count saved but failed to send email report');
          },
        });
      },
      onError: (err: any) => {
        toast.error(err.message || 'Failed to complete count');
      },
    });
  }, [completeCount, sendReport]);

  const handleSendReport = useCallback(() => {
    sendReport.mutate(undefined, {
      onSuccess: () => {
        toast.success('Report sent');
      },
      onError: (err: any) => {
        toast.error(err.message || 'Failed to send report');
      },
    });
  }, [sendReport]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground">Loading stock count...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !count) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4 max-w-md px-6">
          <AlertTriangle className="h-12 w-12 mx-auto text-destructive" />
          <h1 className="text-xl font-semibold">Stock Count Not Found</h1>
          <p className="text-muted-foreground">
            This link may have expired or is invalid. Please contact your manager for a new link.
          </p>
        </div>
      </div>
    );
  }

  const items = count.items || [];
  const isCompleted = count.status === 'completed';

  // Calculate totals
  const totals = items.reduce(
    (acc, item) => {
      const o = item.opening_qty ?? 0;
      const r = item.received_qty ?? 0;
      const c = item.closing_qty ?? 0;
      acc.opening += o;
      acc.received += r;
      acc.closing += c;
      acc.available += o + r;
      if (item.closing_qty !== null) {
        acc.expectedSold += o + r - c;
      }
      if (item.system_sold_qty !== null && item.system_sold_qty !== undefined) {
        acc.systemSold += item.system_sold_qty;
      }
      if (item.variance_qty !== null && item.variance_qty !== undefined) {
        acc.variance += item.variance_qty;
      }
      return acc;
    },
    { opening: 0, received: 0, closing: 0, available: 0, expectedSold: 0, systemSold: 0, variance: 0 }
  );

  const tabs: { key: ActiveView; label: string; icon: React.ElementType }[] = [
    { key: 'opening', label: 'Opening', icon: Package },
    { key: 'received', label: 'Received', icon: Truck },
    { key: 'closing', label: 'Closing', icon: Lock },
    { key: 'summary', label: 'Summary', icon: BarChart3 },
  ];

  const qtyFieldMap: Record<Phase, 'opening_qty' | 'received_qty' | 'closing_qty'> = {
    opening: 'opening_qty',
    received: 'received_qty',
    closing: 'closing_qty',
  };

  const timeFieldMap: Record<Phase, 'opening_time' | 'received_time' | 'closing_time'> = {
    opening: 'opening_time',
    received: 'received_time',
    closing: 'closing_time',
  };

  const lockedMap: Record<Phase, boolean> = {
    opening: count.opening_locked,
    received: count.received_locked,
    closing: count.closing_locked,
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-card border-b shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                <ClipboardList className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h1 className="text-lg font-semibold tracking-tight">Cashier Stock Count</h1>
                <p className="text-xs text-muted-foreground">
                  {count.location_name} &middot; {count.organization_name}
                </p>
              </div>
            </div>
            <div className="text-right shrink-0">
              <p className="text-xs text-muted-foreground">{formatDate(count.created_at)}</p>
              <Badge
                variant={isCompleted ? 'default' : 'secondary'}
                className={cn(
                  'mt-1',
                  isCompleted && 'bg-green-600 hover:bg-green-700'
                )}
              >
                {isCompleted ? 'Completed' : count.status === 'in_progress' ? 'In Progress' : 'Draft'}
              </Badge>
            </div>
          </div>

          {/* Cashier Name Input */}
          <div className="mt-3">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground shrink-0">Cashier:</span>
              {isCompleted ? (
                <span className="text-sm font-medium">{count.cashier_name || 'Not specified'}</span>
              ) : (
                <Input
                  value={cashierNameInput}
                  onChange={(e) => setCashierNameInput(e.target.value)}
                  onBlur={handleCashierNameBlur}
                  placeholder="Enter your name..."
                  className="h-8 text-sm max-w-[200px]"
                />
              )}
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="max-w-4xl mx-auto px-4 pb-3">
          <div className="grid grid-cols-4 gap-2 md:grid-cols-7">
            {[
              { label: 'Opening', value: totals.opening, className: 'text-teal-600 dark:text-teal-400' },
              { label: 'Received', value: totals.received, className: 'text-blue-600 dark:text-blue-400' },
              { label: 'Available', value: totals.available, className: 'text-indigo-600 dark:text-indigo-400' },
              { label: 'Closing', value: totals.closing, className: 'text-violet-600 dark:text-violet-400' },
              { label: 'Sold', value: totals.expectedSold, className: 'text-red-600 dark:text-red-400' },
              { label: 'System', value: totals.systemSold, className: 'text-amber-600 dark:text-amber-400' },
              { label: 'Variance', value: totals.variance, className: cn('text-red-600 dark:text-red-400', totals.variance === 0 && 'text-green-600 dark:text-green-400') },
            ].map((card) => (
              <div
                key={card.label}
                className="bg-muted/50 rounded-lg p-2 text-center"
              >
                <div className={cn('text-[10px] font-semibold uppercase tracking-wider', card.className)}>
                  {card.label}
                </div>
                <div className="text-lg font-bold tabular-nums">{card.value}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Tab Bar */}
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex border-b">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeView === tab.key;
              const isLocked = tab.key !== 'summary' && lockedMap[tab.key as Phase];
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveView(tab.key)}
                  className={cn(
                    'flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors border-b-2 -mb-px',
                    isActive
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  )}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {tab.label}
                  {isLocked && <Check className="h-3 w-3 text-green-500" />}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {count.session_id && (
        <div className="max-w-4xl mx-auto px-4 pt-4">
          <div className="rounded-lg border bg-card p-3 text-sm">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <span>
                Session: <strong>{count.session_status || 'linked'}</strong>
              </span>
              <span>
                Opened: <strong>{count.session_opened_at ? formatTime(count.session_opened_at) : '--'}</strong>
              </span>
              <span>
                Cash Sales: <strong>{count.system_cash_payments ?? 0}</strong>
              </span>
              <span>
                Cash Refunds: <strong>{count.system_cash_refunds ?? 0}</strong>
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-4 pb-24">
        {activeView !== 'summary' && (
          <>
            {/* Locked banner */}
            {lockedMap[activeView] && (
              <div className="flex items-center gap-2 p-3 mb-4 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 text-sm">
                <Check className="h-4 w-4 shrink-0" />
                <span>
                  <strong>{activeView.charAt(0).toUpperCase() + activeView.slice(1)} Stock</strong> has been locked.
                </span>
              </div>
            )}

            {/* Refresh Received Button */}
            {activeView === 'received' && !lockedMap.received && !isCompleted && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefreshReceived}
                disabled={refreshReceived.isPending}
                className="mb-4 w-full"
              >
                {refreshReceived.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Refresh Received from System
              </Button>
            )}

            {/* Add Item (only if not locked and not completed) */}
            {!lockedMap[activeView] && !isCompleted && (
              <div className="flex gap-2 mb-4">
                <Input
                  value={newItemName}
                  onChange={(e) => setNewItemName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddItem()}
                  placeholder="Add new item..."
                  className="h-9 text-sm"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleAddItem}
                  disabled={!newItemName.trim() || addItem.isPending}
                  className="shrink-0"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
            )}

            {/* Column Headers */}
            <div className="grid grid-cols-[1fr_80px_80px_32px] gap-2 px-3 pb-2 border-b text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              <div>Item</div>
              <div className="text-center">Qty</div>
              <div className="text-center">Time</div>
              <div></div>
            </div>

            {/* Items */}
            {items.length === 0 && (
              <div className="py-12 text-center text-muted-foreground text-sm">
                No items in this count. Add items above to get started.
              </div>
            )}

            {items.map((item) => {
              const qtyField = qtyFieldMap[activeView];
              const timeField = timeFieldMap[activeView];
              const { available, expectedSold, systemSold, variance } = calcItem(item);
              const currentQty = item[qtyField];
              const currentTime = item[timeField];

              // System-locked value detection
              const isSystemLocked =
                (activeView === 'opening' && item.system_opening_qty != null) ||
                (activeView === 'received' && item.system_received_qty != null);
              const systemValue =
                activeView === 'opening' ? item.system_opening_qty :
                activeView === 'received' ? item.system_received_qty : null;

              // Closing > available warning
              const closingExceedsAvailable =
                activeView === 'closing' && currentQty !== null && currentQty > available && available > 0;

              return (
                <div
                  key={item.id}
                  className={cn(
                    'grid grid-cols-[1fr_80px_80px_32px] gap-2 px-3 py-2.5 items-center border-b border-border/50',
                    currentQty !== null && 'border-l-2 border-l-primary',
                    closingExceedsAvailable && 'bg-amber-50 dark:bg-amber-950/20 border-l-2 border-l-amber-500'
                  )}
                >
                  <div>
                    <div className="text-sm font-medium flex items-center gap-1.5">
                      {item.name}
                      {isSystemLocked && (
                        <ShieldCheck className="h-3 w-3 text-blue-500 shrink-0" />
                      )}
                    </div>
                    {isSystemLocked && systemValue != null && currentQty !== null && currentQty !== systemValue && (
                      <div className="text-[10px] text-amber-600 dark:text-amber-400 mt-0.5">
                        System: {systemValue} (adjusted{item.adjustment_reason ? `: ${item.adjustment_reason}` : ''})
                      </div>
                    )}
                    {activeView === 'closing' && (
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        Avail: {available}
                        {expectedSold !== null && (
                          <>
                            {' '} / Sold:{' '}
                            <span
                              className={cn(
                                'font-semibold',
                                expectedSold < 0 ? 'text-destructive' : 'text-green-600 dark:text-green-400'
                              )}
                            >
                              {expectedSold}
                            </span>
                          </>
                        )}
                        {systemSold !== null && (
                          <>
                            {' '} / System: <span className="font-semibold text-amber-600 dark:text-amber-400">{systemSold}</span>
                          </>
                        )}
                        {variance !== null && (
                          <>
                            {' '} / Var:{' '}
                            <span className={cn(
                              'font-semibold',
                              variance === 0 ? 'text-green-600 dark:text-green-400' : 'text-destructive'
                            )}>
                              {variance}
                            </span>
                          </>
                        )}
                      </div>
                    )}
                    {closingExceedsAvailable && (
                      <div className="text-[10px] text-amber-600 dark:text-amber-400 font-semibold mt-0.5">
                        Closing ({currentQty}) exceeds available ({available}) - verify count
                      </div>
                    )}
                  </div>
                  {isSystemLocked && !lockedMap[activeView] && !isCompleted ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-9 w-[80px] text-xs"
                      onClick={() => handleOpenAdjust(
                        item.id,
                        item.name,
                        qtyField as 'opening_qty' | 'received_qty',
                        systemValue!,
                        currentQty ?? systemValue!
                      )}
                    >
                      <Pencil className="h-3 w-3 mr-1" />
                      {currentQty ?? systemValue}
                    </Button>
                  ) : (
                    <Input
                      type="number"
                      min="0"
                      value={currentQty ?? ''}
                      onChange={(e) => handleUpdateItem(item.id, qtyField, e.target.value)}
                      disabled={lockedMap[activeView] || isCompleted || (isSystemLocked && !lockedMap[activeView])}
                      placeholder="0"
                      className="h-9 text-center text-sm font-semibold tabular-nums"
                    />
                  )}
                  <div className="text-center text-[11px] text-muted-foreground tabular-nums">
                    {formatTime(currentTime)}
                  </div>
                  {!lockedMap[activeView] && !isCompleted ? (
                    <button
                      onClick={() => handleRemoveItem(item.id)}
                      className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  ) : (
                    <div />
                  )}
                </div>
              );
            })}

            {/* Lock / Submit Phase */}
            {!lockedMap[activeView] && !isCompleted && items.length > 0 && (
              <Button
                onClick={() => handleLockPhase(activeView)}
                disabled={lockPhase.isPending}
                className="w-full mt-4"
                size="lg"
              >
                {lockPhase.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Lock className="h-4 w-4 mr-2" />
                )}
                Lock & Submit {activeView.charAt(0).toUpperCase() + activeView.slice(1)} Stock
              </Button>
            )}
          </>
        )}

        {/* Summary View */}
        {activeView === 'summary' && (
          <div className="space-y-4">
            {/* Info Card */}
            <div className="rounded-lg border bg-card p-4 space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Stock Count Report
              </h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-muted-foreground">Location: </span>
                  <strong>{count.location_name}</strong>
                </div>
                <div>
                  <span className="text-muted-foreground">Cashier: </span>
                  <strong>{count.cashier_name || '--'}</strong>
                </div>
                <div>
                  <span className="text-muted-foreground">Date: </span>
                  <strong>{formatDate(count.created_at)}</strong>
                </div>
                <div>
                  <span className="text-muted-foreground">Status: </span>
                  <Badge
                    variant={isCompleted ? 'default' : 'secondary'}
                    className={cn(isCompleted && 'bg-green-600 hover:bg-green-700')}
                  >
                    {isCompleted
                      ? 'Complete'
                      : count.opening_locked && count.received_locked && count.closing_locked
                        ? 'All Phases Locked'
                        : 'In Progress'}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Summary Table */}
            <div className="rounded-lg border bg-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/50 border-b">
                      <th className="text-left px-3 py-2.5 font-semibold">Item</th>
                      <th className="text-right px-3 py-2.5 font-semibold">Opening</th>
                      <th className="text-right px-3 py-2.5 font-semibold">Received</th>
                      <th className="text-right px-3 py-2.5 font-semibold">Available</th>
                      <th className="text-right px-3 py-2.5 font-semibold">Closing</th>
                      <th className="text-right px-3 py-2.5 font-semibold">Counted Sold</th>
                      <th className="text-right px-3 py-2.5 font-semibold">System Sold</th>
                      <th className="text-right px-3 py-2.5 font-semibold">Variance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => {
                      const { available, expectedSold, systemSold, variance } = calcItem(item);
                      const hasIssue = (expectedSold !== null && expectedSold < 0) || (variance !== null && variance !== 0);
                      return (
                        <tr
                          key={item.id}
                          className={cn(
                            'border-b border-border/50',
                            hasIssue && 'bg-destructive/5'
                          )}
                        >
                          <td className="px-3 py-2 font-medium">
                            {hasIssue && (
                              <AlertTriangle className="h-3 w-3 inline-block mr-1 text-destructive" />
                            )}
                            {item.name}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums font-semibold">
                            {item.opening_qty ?? '--'}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums font-semibold text-blue-600 dark:text-blue-400">
                            {item.received_qty ?? '--'}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums font-semibold text-indigo-600 dark:text-indigo-400">
                            {available}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums font-semibold text-violet-600 dark:text-violet-400">
                            {item.closing_qty ?? '--'}
                          </td>
                          <td
                            className={cn(
                              'px-3 py-2 text-right tabular-nums font-bold',
                              expectedSold === null
                                ? 'text-muted-foreground'
                                : expectedSold < 0
                                  ? 'text-destructive'
                                  : 'text-green-600 dark:text-green-400'
                            )}
                          >
                            {expectedSold ?? '--'}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums font-semibold text-amber-600 dark:text-amber-400">
                            {systemSold ?? '--'}
                          </td>
                          <td
                            className={cn(
                              'px-3 py-2 text-right tabular-nums font-bold',
                              variance === null
                                ? 'text-muted-foreground'
                                : variance === 0
                                  ? 'text-green-600 dark:text-green-400'
                                  : 'text-destructive'
                            )}
                          >
                            {variance ?? '--'}
                          </td>
                        </tr>
                      );
                    })}

                    {/* Totals Row */}
                    <tr className="bg-muted/70 font-bold">
                      <td className="px-3 py-2.5">TOTALS</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{totals.opening}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-blue-600 dark:text-blue-400">
                        {totals.received}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-indigo-600 dark:text-indigo-400">
                        {totals.available}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-violet-600 dark:text-violet-400">
                        {totals.closing}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-primary text-base">
                        {totals.expectedSold}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-amber-600 dark:text-amber-400">
                        {totals.systemSold}
                      </td>
                      <td className={cn(
                        'px-3 py-2.5 text-right tabular-nums text-base',
                        totals.variance === 0 ? 'text-green-600 dark:text-green-400' : 'text-destructive'
                      )}>
                        {totals.variance}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Formula Note */}
            <div className="text-center text-xs text-muted-foreground py-2 bg-muted/30 rounded-lg">
              Counted Sold = (Opening Stock + Stock Received) - Closing Stock. Variance = Counted Sold - System Sold.
            </div>

            {/* Discrepancy Alert */}
            {items.some((item) => {
              const { expectedSold, variance } = calcItem(item);
              return (expectedSold !== null && expectedSold < 0) || (variance !== null && variance !== 0);
            }) && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <div>
                  <strong>ALERT:</strong> The cashier count does not match the system-tracked sales and movement totals.
                  Review stock receipts, refunds, voids, and any unexplained shrinkage before closing the shift.
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-2 pt-2">
              {!isCompleted && count.opening_locked && count.received_locked && count.closing_locked && (
                <Button
                  onClick={() => setShowCompleteDialog(true)}
                  disabled={completeCount.isPending}
                  className="w-full"
                  size="lg"
                >
                  {completeCount.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4 mr-2" />
                  )}
                  Complete & Send Report
                </Button>
              )}

              {isCompleted && count.inventory_synced && (
                <div className="flex items-center justify-center gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 text-sm">
                  <ShieldCheck className="h-4 w-4 shrink-0" />
                  <span>
                    Inventory updated
                    {count.inventory_synced_at && (
                      <> at {formatTime(count.inventory_synced_at)}</>
                    )}
                  </span>
                </div>
              )}

              {!isCompleted && !(count.opening_locked && count.received_locked && count.closing_locked) && (
                <p className="text-center text-sm text-muted-foreground py-4">
                  Lock all three phases (Opening, Received, Closing) to complete and send the report.
                </p>
              )}

              {isCompleted && (
                <Button
                  onClick={handleSendReport}
                  disabled={sendReport.isPending}
                  variant="outline"
                  className="w-full"
                  size="lg"
                >
                  {sendReport.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Resend Report Email
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Adjustment Dialog */}
      <Dialog open={!!adjustDialog} onOpenChange={(open) => !open && setAdjustDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Adjust {adjustDialog?.field === 'opening_qty' ? 'Opening' : 'Received'} Quantity</DialogTitle>
            <DialogDescription>
              The system recorded <strong>{adjustDialog?.systemValue}</strong> for <strong>{adjustDialog?.itemName}</strong>.
              To change this value, provide a reason for the adjustment.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>New Quantity</Label>
              <Input
                type="number"
                min="0"
                value={adjustValue}
                onChange={(e) => setAdjustValue(e.target.value)}
                className="text-center text-lg font-semibold"
              />
            </div>
            <div className="space-y-2">
              <Label>Reason for Adjustment (required)</Label>
              <Input
                value={adjustReason}
                onChange={(e) => setAdjustReason(e.target.value)}
                placeholder="e.g., Physical count differs, item was miscounted"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAdjustDialog(null)}>
                Cancel
              </Button>
              <Button onClick={handleConfirmAdjust} disabled={!adjustReason.trim()}>
                <Check className="h-4 w-4 mr-2" />
                Confirm Adjustment
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Completion Checklist Dialog */}
      <Dialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Complete Stock Count</DialogTitle>
            <DialogDescription>
              Review the following before completing. Closing quantities will update the actual Front of House inventory.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {(() => {
              const varianceItems = items.filter((i) => {
                const { variance } = calcItem(i);
                return variance !== null && variance !== 0;
              });
              const overCountItems = items.filter((i) => {
                const { available } = calcItem(i);
                return i.closing_qty !== null && i.closing_qty > available && available > 0;
              });
              const adjustedItems = items.filter((i) => i.adjustment_reason);

              return (
                <>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/50">
                      <span className="font-medium">{items.length}</span>
                      <span className="text-muted-foreground">total items counted</span>
                    </div>

                    {varianceItems.length > 0 && (
                      <div className="flex items-center gap-2 p-2 rounded-lg bg-red-50 dark:bg-red-950/20 text-red-700 dark:text-red-400">
                        <AlertTriangle className="h-4 w-4 shrink-0" />
                        <span><strong>{varianceItems.length}</strong> items with non-zero variance</span>
                      </div>
                    )}

                    {overCountItems.length > 0 && (
                      <div className="flex items-center gap-2 p-2 rounded-lg bg-amber-50 dark:bg-amber-950/20 text-amber-700 dark:text-amber-400">
                        <AlertTriangle className="h-4 w-4 shrink-0" />
                        <span><strong>{overCountItems.length}</strong> items where closing exceeds available</span>
                      </div>
                    )}

                    {adjustedItems.length > 0 && (
                      <div className="flex items-center gap-2 p-2 rounded-lg bg-blue-50 dark:bg-blue-950/20 text-blue-700 dark:text-blue-400">
                        <Pencil className="h-4 w-4 shrink-0" />
                        <span><strong>{adjustedItems.length}</strong> system values were manually adjusted</span>
                      </div>
                    )}

                    {varianceItems.length === 0 && overCountItems.length === 0 && adjustedItems.length === 0 && (
                      <div className="flex items-center gap-2 p-2 rounded-lg bg-green-50 dark:bg-green-950/20 text-green-700 dark:text-green-400">
                        <Check className="h-4 w-4 shrink-0" />
                        <span>No discrepancies found</span>
                      </div>
                    )}
                  </div>

                  <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/20 p-3 text-xs text-amber-700 dark:text-amber-400">
                    Completing this count will update the Front of House inventory for all counted items.
                    This action cannot be undone.
                  </div>

                  <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" onClick={() => setShowCompleteDialog(false)}>
                      Review Again
                    </Button>
                    <Button onClick={handleComplete} disabled={completeCount.isPending}>
                      {completeCount.isPending ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Check className="h-4 w-4 mr-2" />
                      )}
                      Complete & Update Inventory
                    </Button>
                  </div>
                </>
              );
            })()}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
